import React, { useContext } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link as MuiLink,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_FAQ,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { FAQ_ITEMS } from "../PublicInfo/constants/faqContent";
import FaqAnswer from "../PublicInfo/FaqAnswer";
import AppButton from "../common/AppButton";
import { WELCOME_FAQ_PREVIEW_COUNT } from "./welcomeSectionContent";
import {
  welcomeFaqAccordionSx,
  welcomeSectionContentSx,
  welcomeFaqSectionSx,
  welcomeSectionIntroSx,
  welcomeSectionTitleSx
} from "./welcomeSectionStyles";

const WelcomeFaqSection: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext);
  const faqPath = localizedPath(PAGE_ROUTE_FAQ, language, defaultLang);
  const contactPath = localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang);
  const previewItems = FAQ_ITEMS.slice(0, WELCOME_FAQ_PREVIEW_COUNT);

  return (
    <Box
      component="section"
      data-testid="welcome-faq-section"
      sx={welcomeFaqSectionSx}
    >
      <Box sx={welcomeSectionContentSx}>
        <Typography
          component="h2"
          sx={{ ...welcomeSectionTitleSx, textAlign: "center" }}
        >
          {t("welcome-faq-title", "Frequently asked questions")}
        </Typography>
        <Typography
          sx={{
            ...welcomeSectionIntroSx,
            textAlign: "center",
            maxWidth: 560,
            mx: "auto",
            mb: 4
          }}
        >
          {t("welcome-faq-intro", "Quick answers about Kivunova FMS.")}{" "}
          <MuiLink
            component={Link}
            to={faqPath}
            underline="hover"
            fontWeight={600}
            data-testid="welcome-faq-inline-link"
          >
            {t("welcome-faq-intro-faq-link", "View all FAQs")}
          </MuiLink>{" "}
          {t("welcome-faq-intro-or", "or")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            fontWeight={600}
            data-testid="welcome-faq-contact-link"
          >
            {t("welcome-faq-intro-contact-link", "contact us")}
          </MuiLink>
        </Typography>

        <Box sx={{ maxWidth: 720, mx: "auto" }}>
          {previewItems.map((item, index) => (
            <Accordion
              key={item.id}
              disableGutters
              elevation={0}
              sx={welcomeFaqAccordionSx}
              defaultExpanded={index === 0}
              data-testid={`welcome-faq-${item.id}`}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="primary" />}
                aria-controls={`welcome-faq-${item.id}-content`}
                id={`welcome-faq-${item.id}-header`}
                sx={{ px: 2, py: 0.5 }}
              >
                <Typography fontWeight={600} color="primary.main">
                  {t(item.questionKey, item.questionFallback)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                <FaqAnswer item={item} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box sx={{ textAlign: "center", mt: 4 }}>
          <AppButton
            component={Link}
            to={faqPath}
            variant="primary"
            endIcon={<ArrowForwardIcon />}
            data-testid="welcome-faq-cta"
          >
            {t("welcome-faq-cta", "View all FAQs")}
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default WelcomeFaqSection;
